// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { Check, Close } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { Instrument, InstrumentKey } from '../../../utils/instrument';

type SelectedInstrumentProps = {
  instrument: Instrument;
  onRemove: (instrument: InstrumentKey) => void;
};

const SelectedInstrument = ({ instrument, onRemove }: SelectedInstrumentProps) => {
  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      <Box
        sx={{
          flexDirection: 'column',
          display: 'flex',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        <Typography
          sx={{ textOverflow: 'ellipsis', textTransform: 'uppercase' }}
          variant="caption"
          fontWeight="bold"
          color="secondary"
        >
          {instrument.displayName}
        </Typography>
        <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} variant="caption">
          {instrument.admin}
        </Typography>
      </Box>
      <Box>
        <IconButton size="small" color="error" onClick={() => onRemove(instrument.key)}>
          <Close color="error" fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

type InstrumentMultiSelectProps = {
  instruments: Instrument[];
  getInstrumentsByKeys: (instrumentKeys: InstrumentKey[]) => Instrument[];
  onRemove: (inst: InstrumentKey) => void;
  onClick: (inst: InstrumentKey) => void;
  selectedInstruments: InstrumentKey[];
};

export const InstrumentMultiSelect: React.FC<InstrumentMultiSelectProps> = ({
  instruments,
  getInstrumentsByKeys,
  onClick,
  selectedInstruments,
  onRemove,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const isOpen = Boolean(anchorEl);

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const instrumentsDropdownLabel =
    selectedInstruments.length > 0
      ? getInstrumentsByKeys(selectedInstruments)
          .map((i) => i.displayName.toUpperCase())
          .join(', ')
      : 'Select from listed assets';

  return (
    <Box>
      <Box
        sx={[
          {
            cursor: 'pointer',
            p: 1,
            mb: 2,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: 'divider',
            borderRadius: 1,
          },
          isOpen && {
            borderColor: 'secondary.main',
            borderWidth: 2,
          },
        ]}
        onClick={handleClick}
      >
        <Typography variant="body1">{instrumentsDropdownLabel}</Typography>
        <Box sx={{ ml: 'auto', alignItems: 'center', display: 'flex' }}>
          <ArrowDropDownIcon color="action" />
        </Box>
      </Box>
      <Menu
        id="simple-popover"
        open={isOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={handleClosePopover}
      >
        {instruments.map((instrument) => {
          const isSelected = selectedInstruments.includes(instrument.key);
          return (
            <MenuItem
              sx={[
                {
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: '1px',
                },
                isSelected && {
                  '&:not(&:hover)': {
                    backgroundColor: 'action.selected',
                  },
                },
              ]}
              onClick={() => {
                onClick(instrument.key);
              }}
              key={instrument.id}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  overflow: 'hidden',
                  width: '100%',
                }}
              >
                <Box
                  sx={{ maxWidth: 550, width: '100%', display: 'flex', flexDirection: 'column' }}
                >
                  <Typography
                    variant="caption"
                    sx={{ textTransform: 'uppercase' }}
                    fontWeight="bold"
                  >
                    {instrument.displayName}
                  </Typography>
                  <Typography
                    sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    variant="caption"
                  >
                    {instrument.admin}
                  </Typography>
                </Box>
                <Check
                  sx={{
                    marginLeft: 0.25,
                    visibility: isSelected ? undefined : 'hidden',
                    color: 'inherit',
                  }}
                />
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          height: 160,
          overflowY: 'scroll',
          borderWidth: 1,
          borderColor: 'divider',
          borderStyle: 'solid',
          p: 1.5,
          borderRadius: 1,
        }}
      >
        {selectedInstruments.length === 0 && (
          <Typography
            sx={{
              display: 'flex',
              height: 1,
              width: 1,
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.disabled',
              textTransform: 'uppercase',
            }}
            fontWeight="bold"
            variant="caption"
          >
            No assets selected
          </Typography>
        )}
        {getInstrumentsByKeys(selectedInstruments).map((instrument) => (
          <SelectedInstrument key={instrument.key} instrument={instrument} onRemove={onRemove} />
        ))}
      </Box>
    </Box>
  );
};
